/**
 * 全局提示（toast）的状态容器。
 *
 * 与 React 版的差别：那边只有一个 Context，`push` 必须从组件树里拿；
 * 这里状态住在模块作用域，`useToast()` 返回的 `push` 在任何地方都能调用
 * （包括 api-client 的订阅回调、非组件的 mock 代码）。代价是它不再是
 * "每个 Provider 一份"——本应用全局只有一个 Provider，这个代价是零。
 *
 * `useToast()` 在没有 Provider 时抛错而不是静默丢弃：一个被丢掉的错误提示
 * 会让人以为是"接口没报错"，比崩溃更难查。
 */

import { reactive, readonly } from 'vue';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

export interface ToastInput {
  title: string;
  description?: string;
  tone?: ToastTone;
  /** 0 表示不自动关闭（维护公告这类需要用户主动关掉）。 */
  durationMs?: number;
}

export interface ToastItem extends ToastInput {
  id: number;
}

/** 同时最多显示 3 条：一次错误风暴（比如批量请求全部 42901）不该盖住整个屏幕。 */
export const MAX_VISIBLE = 3;

const DEFAULT_DURATION_MS = 5000;

const state = reactive<{ items: ToastItem[]; provided: boolean }>({ items: [], provided: false });

let nextId = 1;
const timers = new Map<number, ReturnType<typeof setTimeout>>();

export function removeToast(id: number): void {
  const timer = timers.get(id);
  if (timer !== undefined) {
    clearTimeout(timer);
    timers.delete(id);
  }
  state.items = state.items.filter((item) => item.id !== id);
}

export function pushToast(input: ToastInput): void {
  if (!state.provided) {
    throw new Error('useToast 必须在 <ToastProvider> 内使用');
  }

  const id = nextId;
  nextId += 1;
  const item: ToastItem = { tone: 'info', ...input, id };
  state.items = [...state.items, item].slice(-MAX_VISIBLE);

  const duration = input.durationMs ?? DEFAULT_DURATION_MS;
  if (duration > 0) {
    timers.set(
      id,
      setTimeout(() => removeToast(id), duration),
    );
  }
}

/** Provider 挂载时调用，卸载时调用返回的函数——把未触发的定时器清干净。 */
export function markToastProviderMounted(): () => void {
  state.provided = true;
  return () => {
    state.provided = false;
    for (const timer of timers.values()) clearTimeout(timer);
    timers.clear();
    state.items = [];
  };
}

export const toastState = readonly(state);

export function useToast(): (input: ToastInput) => void {
  if (!state.provided) {
    throw new Error('useToast 必须在 <ToastProvider> 内使用');
  }
  return pushToast;
}

export function resetToastForTest(): void {
  for (const timer of timers.values()) clearTimeout(timer);
  timers.clear();
  state.items = [];
  state.provided = false;
  nextId = 1;
}
